import React, { useEffect, useState } from "react";
import { formatRelationshipStatusLabel } from "../utils/enrichmentJobCard";
import {
  RELATIONSHIP_STATUS_OPTIONS,
  normalizeRelationshipStatus,
} from "../utils/relationshipStatus";

const emptyFields = () => ({
  activeFrom: "",
  activeUntil: "",
  status: "Proposed",
  description: "",
});

/** Normalize API / item fields into form state (date inputs want YYYY-MM-DD). */
export const relationshipAttributesFromItem = (item = {}) => {
  const toDateInput = (value) => {
    if (!value) return "";
    const s = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  };
  return {
    activeFrom: toDateInput(item.activeFrom ?? item.active_from),
    activeUntil: toDateInput(item.activeUntil ?? item.active_until),
    status: normalizeRelationshipStatus(item.status || "Proposed"),
    description: String(item.description ?? "").trim(),
  };
};

/** Form fields → API snake_case updates (+ camelCase mirror for local UI). */
export const relationshipAttributesToPayload = (fields = {}) => {
  const activeFrom = String(fields.activeFrom || "").trim() || null;
  const activeUntil = String(fields.activeUntil || "").trim() || null;
  const status = normalizeRelationshipStatus(fields.status || "Proposed");
  const description = String(fields.description || "").trim();
  return {
    api: {
      active_from: activeFrom,
      active_until: activeUntil,
      status,
      description,
    },
    local: {
      activeFrom,
      activeUntil,
      status,
      description,
    },
  };
};

/**
 * Modal to view/edit relationship Active From / Until, Status, Description.
 */
const RelationshipAttributesDialog = ({
  open,
  title = "Relationship details",
  peerName = "",
  initialFields = null,
  saving = false,
  onClose,
  onSave,
}) => {
  const [fields, setFields] = useState(emptyFields);

  useEffect(() => {
    if (!open) return;
    setFields(
      initialFields
        ? { ...emptyFields(), ...initialFields }
        : emptyFields()
    );
    // Hydrate only when the dialog opens (parent remounts via key on item change).
  }, [open]);

  if (!open) return null;

  const handleSave = () => {
    onSave?.(relationshipAttributesToPayload(fields));
  };

  return (
    <div className="fixed inset-0 z-[220] flex items-center justify-center bg-black/60 p-4">
      <div
        className="w-full max-w-lg rounded-lg border border-gray-600 bg-gray-800 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rel-attrs-title"
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-700 px-4 py-3">
          <div className="min-w-0">
            <h3
              id="rel-attrs-title"
              className="text-base font-semibold text-white"
            >
              {title}
            </h3>
            {peerName ? (
              <p className="text-xs text-gray-300 mt-0.5 truncate">{peerName}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="text-gray-400 hover:text-white text-lg leading-none px-1"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="px-4 py-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-300 mb-1">
                Active From
              </label>
              <input
                type="date"
                value={fields.activeFrom}
                onChange={(e) =>
                  setFields((prev) => ({ ...prev, activeFrom: e.target.value }))
                }
                className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-300 mb-1">
                Active Until
              </label>
              <input
                type="date"
                value={fields.activeUntil}
                onChange={(e) =>
                  setFields((prev) => ({
                    ...prev,
                    activeUntil: e.target.value,
                  }))
                }
                className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-300 mb-1">Status</label>
            <select
              value={fields.status}
              onChange={(e) =>
                setFields((prev) => ({ ...prev, status: e.target.value }))
              }
              className="w-full md:max-w-xs bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm"
            >
              {RELATIONSHIP_STATUS_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {formatRelationshipStatusLabel(opt) || opt}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={fields.description}
              onChange={(e) =>
                setFields((prev) => ({ ...prev, description: e.target.value }))
              }
              rows={3}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Optional notes about this relationship"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-700 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-3 py-1.5 text-sm rounded border border-gray-600 text-gray-200 hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RelationshipAttributesDialog;
